// Modules
import { useEffect, useState } from "react";
import { useFormik } from "formik";
import { FormattedMessage, useIntl } from "react-intl";
import * as Yup from "yup";
import get from "lodash.get";
// Material-UI
import { useTheme } from "@mui/material/styles";
import {
    Alert,
    Autocomplete,
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    Stack,
    TextField
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
// Components
import InitializeLocation from "../../../../AppLayout/InitializeLocation";
import AlertDialog from "../../../extended/AlertDialog";
// Helpers
import {
    EVENT_VACATION,
    EVENT_PUBLIC_HOLIDAY,
    EVENT_COMPANY_HOLIDAY,
    EVENT_EDUCATIONAL_LEAVE
} from "../../../../../helpers/constants";
import { buildBatchTherapistSelection, findEventsGuestsAndDates } from "../helpers/calendar";
import { formatDate } from "../../../../../helpers/dates";

// Styles
import { gridSpacing } from "../../../../../styles/constants";
import type {
    AbsenceDialogAlertAction,
    AbsenceDialogAlertState,
    CreateAbsenceSubmitData,
    TherapistAbsenceFormProps,
    UpdateAbsenceSubmitData,
    VacationEvent
} from "@/lib/types/therapist/absence";

const getValidationSchema = type => {
    const baseSchema = {
        therapistsOnVacation: Yup.array()
            .min(1, <FormattedMessage id="error-required-field" />)
            .required(<FormattedMessage id="error-required-field" />),
        vacationStartDate: Yup.date().required(<FormattedMessage id="error-required-field" />),
        vacationEndDate: Yup.date().required(<FormattedMessage id="error-required-field" />)
    };

    if (type === EVENT_VACATION || type === EVENT_EDUCATIONAL_LEAVE) {
        return Yup.object().shape({
            ...baseSchema,
            substituteTherapists: Yup.array().min(1, <FormattedMessage id="error-required-field" />)
        });
    }

    return Yup.object().shape(baseSchema);
};

export const TherapistAbsenceForm = ({
    locale,
    events,
    event,
    therapistsList,
    selectedTherapists: userSelectedTherapists,
    batchTherapistSelection,
    triggerCloseModal,
    createTherapistAbsence,
    updateTherapistAbsence,
    creationType = EVENT_VACATION
}: TherapistAbsenceFormProps) => {
    const intl = useIntl();
    const theme = useTheme();

    const editMode = !!event;

    const isSubstitute = creationType === EVENT_VACATION || creationType === EVENT_EDUCATIONAL_LEAVE;

    const [openAlertDialog, setAlertDialogOpen] = useState<AbsenceDialogAlertState>({
        opened: false,
        action: undefined
    });

    const shouldSelectAllTherapists =
        !editMode && (creationType === EVENT_PUBLIC_HOLIDAY || creationType === EVENT_COMPANY_HOLIDAY);
    const selectedTherapist = userSelectedTherapists?.length === 1 ? [userSelectedTherapists[0]] : [];
    const selectedTherapists = shouldSelectAllTherapists ? therapistsList : selectedTherapist;

    const selectedTherapistIds = selectedTherapist.map(therapist => therapist._id);

    const availableTherapists = therapistsList.filter(therapist => !selectedTherapistIds.includes(therapist._id));

    const createVacationEvent = (values, therapist): VacationEvent => {
        const { vacationStartDate, vacationEndDate, substituteTherapists } = values || {};

        return {
            ...(editMode && { id: event?.id }),
            allDay: true,
            type: creationType,
            title: `${createEventTitle(creationType)} ${therapist?.name || ""}`,
            color: theme.palette.primary.main,
            start: vacationStartDate.toISOString(),
            end: vacationEndDate.toISOString(),
            therapistId: therapist._id,
            ...(isSubstitute && { substitutes: substituteTherapists })
        };
    };

    const createEventTitle = creationType => {
        return intl.formatMessage({ id: creationType });
    };

    const formik = useFormik({
        initialValues: {
            therapistsOnVacation: selectedTherapists,
            vacationStartDate: event?.start ? new Date(event?.start) : null,
            vacationEndDate: event?.end ? new Date(event?.end) : null,
            ...(isSubstitute && { substituteTherapists: event?.substitutes ?? [] })
        },
        validationSchema: getValidationSchema(creationType),
        onSubmit: async values => {
            const { therapistsOnVacation, vacationStartDate, vacationEndDate } = values || {};

            if (!vacationStartDate || !vacationEndDate) {
                return;
            }

            const startFrom = new Date(vacationStartDate);
            const startTo = new Date(vacationEndDate);

            const startDate = formatDate(vacationStartDate, { day: "2-digit", month: "2-digit" });
            const endDate = formatDate(vacationEndDate, { day: "2-digit", month: "2-digit" });

            const successCb = () => {
                handleCloseAlertDialog();
            };

            const errorCb = () => {
                handleCloseAlertDialog();
            };

            const eventsToDelete = findEventsGuestsAndDates(events, startFrom, startTo);

            if (editMode && event?.id) {
                const vacationEvent = createVacationEvent(values, therapistsOnVacation[0]);

                const data: UpdateAbsenceSubmitData = {
                    events: eventsToDelete?.events,
                    guests: eventsToDelete?.guests,
                    vacation: {
                        event: vacationEvent,
                        startDate,
                        endDate
                    }
                };

                updateTherapistAbsence({
                    userId: therapistsOnVacation[0]._id,
                    id: event.id,
                    data,
                    successCb,
                    errorCb
                });
            } else {
                const vacationEvents = therapistsOnVacation.map(therapist => createVacationEvent(values, therapist));

                const data: CreateAbsenceSubmitData = {
                    eventsToDelete: eventsToDelete?.events,
                    guests: eventsToDelete?.guests,
                    vacationEvents,
                    startDate,
                    endDate
                };

                createTherapistAbsence({
                    data,
                    successCb,
                    errorCb
                });
            }
        }
    });

    const handleClickOpenAlertDialog = (action: AbsenceDialogAlertAction) => {
        setAlertDialogOpen({ opened: true, action });
    };
    const handleCloseAlertDialog = () => {
        setAlertDialogOpen(prevState => ({ ...prevState, opened: false }));
        setTimeout(() => {
            setAlertDialogOpen(prevState => ({ ...prevState, action: undefined }));
        }, 500);
    };

    useEffect(() => {
        return () => {
            formik?.resetForm();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { values, errors, touched, isValid, dirty, setFieldValue } = formik;

    const minEndDate = values.vacationStartDate;

    const handleSubmit = e => {
        e.preventDefault();

        formik.handleSubmit(e);
    };

    return (
        <InitializeLocation adapter="date" locale={locale}>
            <form onSubmit={handleSubmit}>
                <DialogTitle>
                    <FormattedMessage id={editMode ? `update-${creationType}` : `create-${creationType}`} />
                </DialogTitle>

                <Divider />
                {therapistsList && (
                    <DialogContent>
                        <Grid container spacing={gridSpacing}>
                            <Grid item xs={8} sx={{ minWidth: "220px" }}>
                                <Autocomplete
                                    multiple
                                    disablePortal
                                    getOptionLabel={option => option.name || ""}
                                    isOptionEqualToValue={(option, value) => option?.name === value?.name}
                                    noOptionsText={<FormattedMessage id="no-options" />}
                                    options={therapistsList || []}
                                    onChange={(_, value) => {
                                        formik?.resetForm();

                                        if (shouldSelectAllTherapists) {
                                            batchTherapistSelection(buildBatchTherapistSelection(value));
                                            setFieldValue("therapistsOnVacation", value);
                                        } else {
                                            const v = value.length > 1 ? [value[value.length - 1]] : value;

                                            setFieldValue("therapistsOnVacation", v);
                                            batchTherapistSelection(buildBatchTherapistSelection(v));
                                        }
                                    }}
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            id="therapistsOnVacation"
                                            name="therapistsOnVacation"
                                            label={
                                                <span>
                                                    <FormattedMessage
                                                        id={
                                                            shouldSelectAllTherapists
                                                                ? "select-therapists"
                                                                : "select-therapist"
                                                        }
                                                    />
                                                    *
                                                </span>
                                            }
                                            error={
                                                get(touched, "therapistsOnVacation", undefined) &&
                                                Boolean(get(errors, "therapistsOnVacation", undefined))
                                            }
                                            helperText={
                                                get(touched, "therapistsOnVacation", undefined) &&
                                                get(errors, "therapistsOnVacation", undefined)
                                            }
                                        />
                                    )}
                                    value={values.therapistsOnVacation}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                )}

                <DialogContent sx={{ p: 3 }}>
                    <Grid container spacing={gridSpacing}>
                        <Grid item xs={6} sx={{ minWidth: "220px" }} data-testid="subsitute-start">
                            <DatePicker
                                slotProps={{ popper: { "data-testid": "subsitute-start-datepicker" } }}
                                format="dd/MM/yyyy"
                                label={
                                    <span>
                                        <FormattedMessage id={"start-date"} />
                                        {"*"}
                                    </span>
                                }
                                minDate={new Date()}
                                textField={({ ...props }) => {
                                    return (
                                        <TextField
                                            {...props}
                                            fullWidth
                                            error={touched.vacationStartDate && Boolean(errors.vacationStartDate)}
                                            helperText={
                                                touched.vacationStartDate &&
                                                errors.vacationStartDate &&
                                                (typeof errors.vacationStartDate === "string" ? (
                                                    <FormattedMessage id="error-invalid-date" />
                                                ) : (
                                                    errors.vacationStartDate
                                                ))
                                            }
                                        />
                                    );
                                }}
                                onChange={value => {
                                    value && value?.setHours(0, 0, 0, 0);
                                    setFieldValue("vacationStartDate", value);
                                    setFieldValue("vacationEndDate", null);
                                }}
                                value={values.vacationStartDate}
                                error={touched.vacationStartDate && Boolean(errors.vacationStartDate)}
                            />
                        </Grid>
                        <Grid item xs={6} sx={{ minWidth: "220px" }} data-testid="subsitute-end">
                            <DatePicker
                                slotProps={{ popper: { "data-testid": "subsitute-end-datepicker" } }}
                                format="dd/MM/yyyy"
                                label={
                                    <span>
                                        <FormattedMessage id={"end-date"} />
                                        {"*"}
                                    </span>
                                }
                                onChange={value => {
                                    value && value.setHours(22, 59, 59, 999);
                                    setFieldValue("vacationEndDate", value);
                                }}
                                minDate={minEndDate}
                                okText={<FormattedMessage id="ok" />}
                                cancelText={<FormattedMessage id="cancel" />}
                                textField={({ ...props }) => {
                                    return (
                                        <TextField
                                            {...props}
                                            fullWidth
                                            error={touched.vacationEndDate && Boolean(errors.vacationEndDate)}
                                            helperText={
                                                touched.vacationEndDate &&
                                                errors.vacationEndDate &&
                                                (typeof errors.vacationEndDate === "string" ? (
                                                    <FormattedMessage id="error-invalid-date" />
                                                ) : (
                                                    errors.vacationEndDate
                                                ))
                                            }
                                        />
                                    );
                                }}
                                value={values.vacationEndDate}
                                disabled={!values.vacationStartDate}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>

                {isSubstitute && therapistsList && (
                    <DialogContent>
                        <Grid container spacing={gridSpacing}>
                            <Grid item xs={12} sx={{ minWidth: "220px" }}>
                                <Autocomplete
                                    id="therapist-list"
                                    multiple
                                    disablePortal
                                    disabled={availableTherapists.length === 0}
                                    getOptionLabel={option => option.name || ""}
                                    isOptionEqualToValue={(option, value) => option?.name === value?.name}
                                    noOptionsText={<FormattedMessage id="no-options" />}
                                    options={availableTherapists || []}
                                    onChange={(_, value) => {
                                        setFieldValue("substituteTherapists", value);
                                    }}
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            id="substituteTherapists"
                                            name="substituteTherapists"
                                            label={
                                                <span>
                                                    <FormattedMessage id="substitute" />
                                                </span>
                                            }
                                            error={
                                                get(touched, "substituteTherapists", undefined) &&
                                                Boolean(get(errors, "substituteTherapists", undefined))
                                            }
                                            helperText={
                                                get(touched, "substituteTherapists", undefined) &&
                                                get(errors, "substituteTherapists", undefined)
                                            }
                                        />
                                    )}
                                    value={get(values, "substituteTherapists", [])}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                )}

                <Alert severity="warning" sx={{ pl: 3, pb: 2 }}>
                    <FormattedMessage id="create-absence-warning" />
                </Alert>
                <Divider />
                <DialogActions sx={{ p: 3 }}>
                    <Grid container justifyContent="space-between" alignItems="center">
                        <Grid item>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Button
                                    type="button"
                                    variant="outlined"
                                    onClick={() => {
                                        if (shouldSelectAllTherapists) {
                                            batchTherapistSelection({});
                                        }
                                        triggerCloseModal();
                                    }}
                                >
                                    <FormattedMessage id="cancel" />
                                </Button>
                                <Button
                                    type="button"
                                    variant="contained"
                                    disabled={!isValid || !dirty}
                                    onClick={e => {
                                        handleClickOpenAlertDialog({
                                            action: () => {
                                                handleSubmit(e);
                                            },
                                            label: "create",
                                            title: "create-vacation-warning-title",
                                            confirmation: `create-${creationType}-warning-confirmation`
                                        });
                                    }}
                                    data-testid={`create-${creationType}`}
                                >
                                    <FormattedMessage id={editMode ? "update" : "create"} />
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </DialogActions>

                <AlertDialog
                    open={openAlertDialog?.opened || false}
                    handleClose={handleCloseAlertDialog}
                    actions={{
                        primary: openAlertDialog?.action,
                        secondary: {
                            action: handleCloseAlertDialog,
                            label: "cancel"
                        }
                    }}
                    title={openAlertDialog?.action?.title}
                    confirmation={openAlertDialog?.action?.confirmation}
                />
            </form>
        </InitializeLocation>
    );
};
